import { useEffect, useState, useCallback } from "react";
import { ShieldQuestion, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "à l'instant";
  if (d < 3600) return `il y a ${Math.floor(d / 60)} min`;
  if (d < 86400) return `il y a ${Math.floor(d / 3600)} h`;
  return `il y a ${Math.floor(d / 86400)} j`;
}

export function useAccessRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState("");
  const isAdmin = user?.role === "admin";

  const reload = useCallback(() => {
    if (!isAdmin) return;
    api.myAccessRequests().then((d) => setRequests(d.requests || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    reload();
    const t = setInterval(reload, 60000);
    return () => clearInterval(t);
  }, [isAdmin, reload]);

  const respond = async (id, consent) => {
    setBusy(id + consent);
    try {
      await api.respondAccessRequest(id, consent);
      toast.success(consent ? "Accès accordé au créateur" : "Demande d'accès refusée",
        { description: consent ? "L'accès expirera automatiquement dans 30 jours." : "" });
      setRequests((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    } finally { setBusy(""); }
  };

  return { requests, respond, reload, busy, isAdmin };
}

export function AccessRequestList({ requests, respond, busy, compact }) {
  if (!requests.length) return null;
  return (
    <div data-testid="access-request-list">
      {requests.map((r) => (
        <div key={r.id} className="notif-item" style={{ alignItems: "flex-start", cursor: "default", flexWrap: "wrap" }} data-testid="access-request-item">
          <div className="notif-ico" style={{ color: "var(--gold)" }}><ShieldQuestion size={15} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="notif-title">Demande d'accès à votre espace</div>
            <div className="notif-detail" style={{ whiteSpace: "normal" }}>
              <b style={{ color: "var(--text)" }}>{r.requested_by_name}</b> — {r.reason}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="btn btn-gold btn-sm" disabled={!!busy} onClick={() => respond(r.id, true)} data-testid="access-grant-btn">
                {busy === r.id + true ? <Loader2 size={13} className="spin" /> : <Check size={13} />} Accorder
              </button>
              <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => respond(r.id, false)} data-testid="access-deny-btn">
                {busy === r.id + false ? <Loader2 size={13} className="spin" /> : <X size={13} />} Refuser
              </button>
            </div>
          </div>
          {!compact && <div className="notif-time">{timeAgo(r.created_at)}</div>}
        </div>
      ))}
    </div>
  );
}

export function AccessRequestsCard() {
  const { requests, respond, busy, isAdmin } = useAccessRequests();
  if (!isAdmin) return null;
  return (
    <div className="card gold-border fade-in" style={{ marginBottom: 24 }} data-testid="access-requests-card">
      <div className="section-title" style={{ marginTop: 0 }}>
        <ShieldQuestion className="ico" size={18} /> Demandes d'accès du créateur
        {requests.length > 0 && <span className="badge badge-gold" style={{ marginLeft: 8 }}>{requests.length}</span>}
      </div>
      <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>
        Conformité Loi 25 — l'équipe PROGÈRE ne peut accéder à votre espace qu'avec votre consentement explicite. Accordez ou refusez directement ici.
      </p>
      {requests.length === 0
        ? <div style={{ color: "var(--muted)", fontSize: 13 }} data-testid="access-requests-empty">Aucune demande d'accès en attente.</div>
        : <AccessRequestList requests={requests} respond={respond} busy={busy} />}
    </div>
  );
}
